import { action, computed, makeObservable, observable, runInAction } from 'mobx';
const DEFAULT_QUERY_DEBOUNCE_MS = 30;
const DEFAULT_CONTENT_REFRESH_DEBOUNCE_MS = 250;
export function getNextUniversalSearchResultIndex(currentResultIndex, resultCount) {
    if (resultCount < 1) {
        return -1;
    }
    const next = currentResultIndex + 1;
    return next >= resultCount ? 0 : next;
}
export function getPreviousUniversalSearchResultIndex(currentResultIndex, resultCount) {
    if (resultCount < 1) {
        return -1;
    }
    const previous = currentResultIndex - 1;
    return previous < 0 ? resultCount - 1 : previous;
}
function errorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return 'Search failed';
}
export class UniversalSearchController {
    constructor(provider, options = {}) {
        var _a, _b;
        this.provider = provider;
        this.isOpen = false;
        this.query = '';
        this.replaceQuery = '';
        this.results = [];
        this.currentResultIndex = -1;
        this.status = 'idle';
        this.error = undefined;
        this.isCaseSensitive = false;
        this.isReplaceMode = false;
        this.locked = false;
        this.searchId = 0;
        this.open = () => {
            this.isOpen = true;
            void this.executeSearch();
        };
        this.close = () => {
            void this.selectCurrentResult({ selectInEditor: true });
            this.clearSessionState();
        };
        this.resetContext = () => {
            this.clearSessionState();
        };
        this.setQuery = (query) => {
            this.query = query;
            if (!query) {
                this.cancelScheduledSearch();
                void this.executeSearch();
                return;
            }
            this.scheduleSearch(this.searchDebounceMs);
        };
        this.refreshSearch = () => {
            if (!this.isOpen || !this.query) {
                return;
            }
            this.scheduleSearch(this.contentSearchDebounceMs);
        };
        this.setReplaceQuery = (replaceQuery) => {
            this.replaceQuery = replaceQuery;
        };
        this.toggleCaseSensitivity = () => {
            if (!this.isOpen) {
                return;
            }
            this.isCaseSensitive = !this.isCaseSensitive;
            this.scheduleSearch(this.searchDebounceMs);
        };
        this.toggleReplaceMode = () => {
            if (!this.canReplace) {
                this.closeReplaceMode();
                return;
            }
            this.isReplaceMode = !this.isReplaceMode;
        };
        this.closeReplaceMode = () => {
            this.isReplaceMode = false;
        };
        this.setLocked = (locked) => {
            this.locked = locked;
            if (locked) {
                this.closeReplaceMode();
            }
        };
        this.setShouldHighlightAll = (shouldHighlightAll) => {
            this.shouldHighlightAll = this.provider.capabilities.supportsHighlightAll && shouldHighlightAll;
        };
        this.goToNextResult = () => {
            this.currentResultIndex = getNextUniversalSearchResultIndex(this.currentResultIndex, this.results.length);
            void this.selectCurrentResult({ scrollIntoView: true });
        };
        this.goToPreviousResult = () => {
            this.currentResultIndex = getPreviousUniversalSearchResultIndex(this.currentResultIndex, this.results.length);
            void this.selectCurrentResult({ scrollIntoView: true });
        };
        this.selectCurrentResult = async (options) => {
            var _a, _b;
            const result = this.currentResult;
            if (!result) {
                return;
            }
            await this.provider.selectResult(result, {
                scrollIntoView: (_a = options === null || options === void 0 ? void 0 : options.scrollIntoView) !== null && _a !== void 0 ? _a : false,
                selectInEditor: (_b = options === null || options === void 0 ? void 0 : options.selectInEditor) !== null && _b !== void 0 ? _b : false,
            });
        };
        this.replaceCurrentResult = async () => {
            if (!this.canReplace || !this.provider.replaceCurrentResult) {
                return;
            }
            const result = this.currentResult;
            if (!result || !this.replaceQuery) {
                return;
            }
            const indexBeforeReplace = this.currentResultIndex;
            const nextResults = await this.provider.replaceCurrentResult(result, {
                query: this.query,
                isCaseSensitive: this.isCaseSensitive,
                replaceQuery: this.replaceQuery,
            });
            await this.handleReplaceResult(nextResults, { preferredIndex: indexBeforeReplace });
        };
        this.replaceAllResults = async () => {
            if (!this.canReplace || !this.provider.replaceAllResults) {
                return;
            }
            if (this.results.length < 1 || !this.replaceQuery) {
                return;
            }
            const nextResults = await this.provider.replaceAllResults(this.results, {
                query: this.query,
                isCaseSensitive: this.isCaseSensitive,
                replaceQuery: this.replaceQuery,
            });
            await this.handleReplaceResult(nextResults);
        };
        this.clearSessionState = () => {
            this.cancelScheduledSearch();
            this.searchId++;
            void this.provider.clear();
            this.isOpen = false;
            this.query = '';
            this.replaceQuery = '';
            this.results = [];
            this.currentResultIndex = -1;
            this.status = 'idle';
            this.error = undefined;
            this.isCaseSensitive = false;
            this.isReplaceMode = false;
            this.shouldHighlightAll = this.provider.capabilities.supportsHighlightAll ? true : false;
        };
        this.cancelScheduledSearch = () => {
            if (this.searchDebounceTimeout) {
                clearTimeout(this.searchDebounceTimeout);
                this.searchDebounceTimeout = undefined;
            }
        };
        this.scheduleSearch = (debounceMs) => {
            this.cancelScheduledSearch();
            if (debounceMs <= 0) {
                void this.executeSearch();
                return;
            }
            this.searchDebounceTimeout = setTimeout(() => {
                this.searchDebounceTimeout = undefined;
                void this.executeSearch();
            }, debounceMs);
        };
        this.executeSearch = async () => {
            if (!this.isOpen) {
                return;
            }
            const searchId = this.searchId + 1;
            this.searchId = searchId;
            if (!this.query) {
                void this.provider.clear();
                this.clearResults('idle');
                return;
            }
            this.status = 'loading';
            this.error = undefined;
            try {
                const results = await this.provider.search({
                    query: this.query,
                    isCaseSensitive: this.isCaseSensitive,
                });
                if (searchId !== this.searchId) {
                    return;
                }
                runInAction(() => {
                    this.setSearchResults(results, { selectResult: true, scrollIntoView: false });
                });
            }
            catch (error) {
                if (searchId !== this.searchId) {
                    return;
                }
                runInAction(() => {
                    this.setSearchError(errorMessage(error));
                });
            }
        };
        this.handleReplaceResult = async (nextResults, options) => {
            if (Array.isArray(nextResults)) {
                runInAction(() => {
                    this.setSearchResults(nextResults, {
                        preferredIndex: options === null || options === void 0 ? void 0 : options.preferredIndex,
                        selectResult: true,
                        scrollIntoView: true,
                    });
                });
                return;
            }
            const results = await this.provider.search({
                query: this.query,
                isCaseSensitive: this.isCaseSensitive,
            });
            runInAction(() => {
                this.setSearchResults(results, {
                    preferredIndex: options === null || options === void 0 ? void 0 : options.preferredIndex,
                    selectResult: true,
                    scrollIntoView: true,
                });
            });
        };
        this.searchDebounceMs = (_a = options.searchDebounceMs) !== null && _a !== void 0 ? _a : DEFAULT_QUERY_DEBOUNCE_MS;
        this.contentSearchDebounceMs = (_b = options.contentSearchDebounceMs) !== null && _b !== void 0 ? _b : DEFAULT_CONTENT_REFRESH_DEBOUNCE_MS;
        this.shouldHighlightAll = provider.capabilities.supportsHighlightAll ? true : false;
        makeObservable(this, {
            isOpen: observable,
            query: observable,
            replaceQuery: observable,
            results: observable,
            currentResultIndex: observable,
            status: observable,
            error: observable,
            isCaseSensitive: observable,
            isReplaceMode: observable,
            shouldHighlightAll: observable,
            locked: observable,
            canReplace: computed,
            currentResult: computed,
            open: action,
            close: action,
            resetContext: action,
            setQuery: action,
            setReplaceQuery: action,
            toggleCaseSensitivity: action,
            toggleReplaceMode: action,
            closeReplaceMode: action,
            setLocked: action,
            setShouldHighlightAll: action,
            goToNextResult: action,
            goToPreviousResult: action,
            setSearchResults: action,
            setSearchError: action,
            clearResults: action,
        });
    }
    get canReplace() {
        return this.provider.capabilities.supportsReplace && !this.locked;
    }
    get currentResult() {
        return this.results[this.currentResultIndex];
    }
    deinit() {
        this.cancelScheduledSearch();
        this.searchId++;
        void this.provider.clear();
        this.results = [];
    }
    setSearchResults(results, options) {
        var _a;
        this.results = results;
        if (results.length < 1) {
            this.currentResultIndex = -1;
        }
        else if ((options === null || options === void 0 ? void 0 : options.preferredIndex) !== undefined) {
            this.currentResultIndex = Math.min(Math.max(0, options.preferredIndex), results.length - 1);
        }
        else {
            this.currentResultIndex = 0;
        }
        this.status = 'ready';
        this.error = undefined;
        if ((options === null || options === void 0 ? void 0 : options.selectResult) && this.currentResultIndex >= 0) {
            void this.selectCurrentResult({ scrollIntoView: (_a = options.scrollIntoView) !== null && _a !== void 0 ? _a : false });
        }
    }
    setSearchError(error) {
        this.results = [];
        this.currentResultIndex = -1;
        this.status = 'error';
        this.error = error;
    }
    clearResults(status) {
        this.results = [];
        this.currentResultIndex = -1;
        this.status = status;
        this.error = undefined;
    }
}
